"use client";

// useHDragScroll — make any horizontally-scrollable element pan with a pointer
// click-and-drag (a carousel grab), on top of native wheel / touch scrolling.
// Desktop gets the grab affordance it otherwise lacks; touch keeps the browser's
// own momentum (we don't capture there, so iOS -webkit-overflow-scrolling still
// owns the fling). On release we hand the remaining velocity to a tiny rAF
// inertia so the strip glides to rest instead of stopping dead.
//
//   const { onPointerDown, dragging, didDrag } = useHDragScroll(ref);
//   <div ref={ref} onPointerDown={onPointerDown} style={{ overflowX: "auto" }} />
//
// `didDrag()` returns true if the just-finished pointer interaction actually
// panned (so a click handler on a child can bail and not fire a navigation).

import { useCallback, useEffect, useRef, useState } from "react";

const DRAG_THRESHOLD = 4; // px of travel before it's a drag, not a click
const FRICTION = 0.92; // per-frame velocity decay for the release glide
const MIN_VELOCITY = 0.05; // px/frame below which inertia stops

export function useHDragScroll(ref: React.RefObject<HTMLElement | null>) {
  const [dragging, setDragging] = useState(false);
  const st = useRef({
    active: false,
    moved: false,
    startX: 0,
    startScroll: 0,
    lastX: 0,
    lastT: 0,
    vel: 0,
    pointerId: -1,
  });
  const raf = useRef<number | null>(null);
  // remembers whether the LAST completed interaction was a pan (read by a
  // child's onClick to suppress an accidental navigation).
  const lastWasDrag = useRef(false);

  const stopInertia = useCallback(() => {
    if (raf.current != null) {
      cancelAnimationFrame(raf.current);
      raf.current = null;
    }
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // ignore secondary buttons; don't hijack a touch fling (browser owns it)
      // or a press on a no-drag control / a chip being reordered.
      if (e.button !== 0 || e.pointerType === "touch") return;
      const el = ref.current;
      if (!el) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest?.("[data-noswipe],[data-reorder],input,textarea,select")) return;
      if (el.scrollWidth <= el.clientWidth + 1) return; // nothing to pan

      stopInertia();
      const s = st.current;
      s.active = true;
      s.moved = false;
      s.startX = e.clientX;
      s.startScroll = el.scrollLeft;
      s.lastX = e.clientX;
      s.lastT = e.timeStamp;
      s.vel = 0;
      s.pointerId = e.pointerId;
      lastWasDrag.current = false;
    },
    [ref, stopInertia],
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onMove = (e: PointerEvent) => {
      const s = st.current;
      if (!s.active || e.pointerId !== s.pointerId) return;
      const dx = e.clientX - s.startX;
      if (!s.moved && Math.abs(dx) < DRAG_THRESHOLD) return;
      if (!s.moved) {
        s.moved = true;
        lastWasDrag.current = true;
        setDragging(true);
        try {
          el.setPointerCapture(s.pointerId);
        } catch {
          /* capture is best-effort */
        }
      }
      e.preventDefault();
      el.scrollLeft = s.startScroll - dx;
      // instantaneous velocity (px/ms) for the release glide
      const dt = e.timeStamp - s.lastT || 16;
      s.vel = (e.clientX - s.lastX) / dt;
      s.lastX = e.clientX;
      s.lastT = e.timeStamp;
    };

    const glide = () => {
      const s = st.current;
      // velocity is px/ms → ~px/frame at 60fps; pan opposes finger direction.
      let v = -s.vel * 16;
      const step = () => {
        if (Math.abs(v) < MIN_VELOCITY) {
          raf.current = null;
          return;
        }
        el.scrollLeft += v;
        v *= FRICTION;
        if (el.scrollLeft <= 0 || el.scrollLeft >= el.scrollWidth - el.clientWidth) {
          raf.current = null;
          return;
        }
        raf.current = requestAnimationFrame(step);
      };
      raf.current = requestAnimationFrame(step);
    };

    const end = (e: PointerEvent) => {
      const s = st.current;
      if (!s.active || e.pointerId !== s.pointerId) return;
      const wasDragging = s.moved;
      s.active = false;
      s.pointerId = -1;
      if (wasDragging) {
        setDragging(false);
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        if (Math.abs(s.vel) > 0.02) glide();
        // clear the "was a drag" flag after the click that follows this pointerup
        // has had a chance to read it.
        setTimeout(() => {
          lastWasDrag.current = false;
        }, 0);
      }
    };

    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
  }, [ref]);

  useEffect(() => stopInertia, [stopInertia]);

  const didDrag = useCallback(() => lastWasDrag.current, []);

  return { onPointerDown, dragging, didDrag };
}

export default useHDragScroll;
